import Vue from "vue";
import axios from "axios";
import {reactive, toRefs, computed, provide, inject} from "@vue/composition-api";
import {mergeGroups} from "../store_helpers";

import {$t} from "@/i18n";

export const state = reactive({
    list: [],
    member_roles: {
        owner: $t("Owner"),
        admin: $t("Admin"),
        user: $t("User"),
        no_role: $t("None"),
    }
});

export const actions = {
    async loadMembers() {
        const resp = await axios.get("/members")
        resp.data.members.sort((a, b) => a.name.localeCompare(b.name));
        state.list = resp.data.members
    },
    getActiveMembersByCompany(companyId) {
        return state.list.filter(x => x.account_id === companyId && x.status === 'active')
    },
    getVisibleMembersByCompany(companyId) {
        return state.list.filter(x => x.account_id === companyId && ['active', 'pending', 'limited'].includes(x.status))
    },
    getVisiblePeopleByCompany(companyId) {
        return state.list.filter(x => x.account_id === companyId && ['active', 'pending', 'limited'].includes(x.status) && x.type === 'person')
    },
    getVisibleMembers() {
        return state.list.filter(x => ['active', 'pending', 'limited'].includes(x.status))
    },
    getGroupsByCompany(companyId) {
        return state.list.filter(x => x.account_id === companyId && x.type === 'group')
    },
    getMemberById(personId) {
        return state.list.find(x => x.id === personId)
    },
    getMembersByIds(personIds) {
        const ret = []

        for (const person of state.list) {
            if (!ret.some(x => x.id === person.id) && personIds.includes(person.id)) {
                ret.push(person)
            }
        }

        return ret
    },
    fetchAccountUsers(account_id, params) {
        return new Promise(function (resolve, reject) {
            axios
                .get("a/{0}/users".format(account_id), {params: params})
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(ex => reject(ex));
        });
    },
    fetchAccountGroup(group_id) {
        return new Promise(function (resolve, reject) {
            axios
                .get("groups/{0}".format(group_id))
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    saveAccountGroup(data) {
        return new Promise(function (resolve, reject) {
            axios
                .post("groups/{0}".format(data.id), data)
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    deleteAccountGroup(group_id) {
        return new Promise(function (resolve, reject) {
            axios
                .delete("groups/{0}".format(group_id))
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    resendUserInvite(personId, companyId, name) {
        return new Promise(function (resolve, reject) {
            let data = {
                name: name,
                person_id: personId,
                company_id: companyId
            };

            axios
                .post(Vue.config.url_prefix + "/api/profile/reinvite", data)
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    fetchMember(company_id, person_id) {
        return new Promise(function (resolve, reject) {
            axios
                .get(Vue.config.url_prefix + "/api/a/{0}/account_members/{1}".format(company_id, person_id))
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    },
    updateMember(company_id, data) {
        return new Promise(function (resolve, reject) {
            axios
                .post(Vue.config.url_prefix + "/api/a/{0}/update_member/".format(company_id), data)
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    },
    activateMember(data) {
        return new Promise(function (resolve, reject) {
            axios
                .post(Vue.config.url_prefix + "/api/activate_member/", data)
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    },
    deactivateMember(data) {
        return new Promise(function (resolve, reject) {
            axios
                .post(Vue.config.url_prefix + "/api/deactivate_member/", data)
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    },
};

export const getters = {
    users: computed(() => state.list),
    usersOwnerFirst: computed(() => {
        const index = state.list.findIndex(user => user.role === "owner");
        if (index > -1) {
            const owner = state.list.splice(index, 1);
            state.list.unshift(...owner);
            return state.list;
        } else {
            return state.list;
        }
    }),
    adminUsers: computed(() => state.list.filter(user => user.role === "admin")),
    total_users: computed(() => state.total),
    activeTotalUsers: computed(() => state.activeTotal),
    visibleMembers: computed(() => {
        const list = []
        for (const member of state.list) {
            if (!['active', 'pending', 'limited'].includes(member.status)) {
                continue
            }

            if (!list.find(x => x.id === member.id)) {
                list.push(member)
            }
        }

        return list
    }),
    allMembers: computed(() => state.list),
    allGroups: computed(() => state.list.filter(x => x.type === 'group'))
};

export const key = Symbol("UserModule");

export const provideUser = () =>
    provide(key, {
        ...toRefs(state),
        ...toRefs(getters),
        ...actions
    });

export default () => inject(key);
